import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { User, Eye, Gauge, LogOut, Save, Ship, Shield, Bell, Camera, Crown, Trash2, Upload, AlertTriangle, Gift, Copy, Share2, Anchor } from 'lucide-react';
import { toast } from 'sonner';
import NotificationSettings from '@/components/NotificationSettings';
import { profileAPI, authAPI } from '@/lib/api';

const SettingsScreen = ({ onOpenAdmin }) => {
  const { user, updateUser, logout, isPaid } = useApp();

  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [username, setUsername] = useState(user?.username || '');
  const [visibilityMode, setVisibilityMode] = useState(user?.visibility_mode || 'FRIENDS_ONLY');
  const [units, setUnits] = useState(user?.units || 'KNOTS');
  const [boatDraftFt, setBoatDraftFt] = useState(user?.boat_draft_ft || '');
  const [isLoading, setIsLoading] = useState(false);
  
  // Boat photo state
  const [boatPhoto, setBoatPhoto] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoVisibility, setPhotoVisibility] = useState(user?.boat_photo_visibility || 'crew');
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPhotoVisibility(user?.boat_photo_visibility || 'crew');
  }, [user?.boat_photo_visibility]);

  const handlePhotoVisibilityChange = async (value) => {
    setPhotoVisibility(value);
    try {
      await updateUser({ boat_photo_visibility: value });
      toast.success(value === 'public' ? 'Boat photo visible to everyone on the map' : 'Boat photo visible to crew only');
    } catch (e) {
      toast.error('Could not update photo visibility');
    }
  };

  // Load boat photo on mount
  useEffect(() => {
    const loadPhoto = async () => {
      try {
        const response = await profileAPI.getPhoto();
        if (response.data.photo_url) {
          setBoatPhoto(response.data.photo_url);
        }
      } catch (error) {
        console.error('Failed to load boat photo:', error);
      }
    };
    loadPhoto();
  }, []);

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type (allow more formats)
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/gif'];
    const fileExt = file.name.toLowerCase().split('.').pop();
    const allowedExts = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'gif'];
    
    if (!allowedTypes.includes(file.type) && !allowedExts.includes(fileExt)) {
      toast.error('Please select a JPEG, PNG, WebP, or HEIC image');
      return;
    }
    
    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      toast.error(`Photo is too large (${sizeMB}MB). Maximum is 5MB.`);
      return;
    }
    
    setPhotoLoading(true);
    try {
      const response = await profileAPI.uploadPhoto(file);
      setBoatPhoto(response.data.photo_url);
      try { await updateUser({}); } catch (e) { /* refresh context user (own map marker) */ }
      toast.success('Boat photo uploaded!');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to upload photo. Please try again.';
      toast.error(detail);
      console.error('Photo upload error:', error.response?.data || error);
    } finally {
      setPhotoLoading(false);
      // Reset file input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm('Delete your boat photo?')) return;
    
    setPhotoLoading(true);
    try {
      await profileAPI.deletePhoto();
      setBoatPhoto(null);
      try { await updateUser({}); } catch (e) { /* refresh context user */ }
      toast.success('Photo deleted');
    } catch (error) {
      toast.error('Failed to delete photo');
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await updateUser({
        display_name: displayName,
        username,
        visibility_mode: visibilityMode,
        units,
        boat_draft_ft: boatDraftFt ? parseFloat(boatDraftFt) : null,
      });
      toast.success('Settings saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setIsLoading(false);
    }
  };

  const [draftSaving, setDraftSaving] = useState(false);

  const handleSaveDraft = async () => {
    setDraftSaving(true);
    try {
      const updated = await updateUser({
        boat_draft_ft: boatDraftFt === '' || boatDraftFt === null ? null : parseFloat(boatDraftFt),
      });
      // keep input in sync with what the server stored
      setBoatDraftFt(updated?.boat_draft_ft ?? '');
      toast.success(boatDraftFt === '' ? 'Boat draft cleared' : 'Boat draft saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save boat draft');
    } finally {
      setDraftSaving(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logout();
    }
  };

  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const handleDeleteAccount = async () => {
    // Double confirmation for destructive action
    const firstConfirm = window.confirm(
      'Are you sure you want to delete your account?\n\nThis will permanently delete:\n• Your profile and settings\n• All trips and float plans\n• Friends and connections\n• All reports you\'ve submitted\n\nThis action CANNOT be undone.'
    );
    
    if (!firstConfirm) return;
    
    const secondConfirm = window.confirm(
      'FINAL WARNING: Your account will be permanently deleted.\n\nType "DELETE" in the next prompt to confirm.'
    );
    
    if (!secondConfirm) return;
    
    const typed = window.prompt('Type DELETE to confirm account deletion:');
    if (typed !== 'DELETE') {
      toast.error('Account deletion cancelled - confirmation text did not match');
      return;
    }
    
    setDeleteLoading(true);
    try {
      await authAPI.deleteAccount();
      toast.success('Account deleted successfully');
      // Wait a moment for the toast to show, then logout
      setTimeout(() => {
        logout();
      }, 1500);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to delete account. Please try again.';
      toast.error(detail);
      console.error('Account deletion error:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50" data-testid="settings-screen">
      {/* Header */}
      <div className="bg-white border-b px-4 py-4">
        <h1 className="text-xl font-bold text-gray-800">Settings</h1>
        <p className="text-sm text-gray-500">Manage your account</p>
      </div>

      <ScrollArea className="flex-1 px-4 py-4">
        {/* Profile */}
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-cyan-500" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="display-name">Display Name</Label>
              <Input
                id="display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="mt-2"
                data-testid="display-name-input"
              />
            </div>
            <div>
              <Label htmlFor="username">Username</Label>
              <div className="relative mt-2">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  className="pl-8"
                  data-testid="username-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Lowercase letters, numbers, underscores only</p>
            </div>
          </CardContent>
        </Card>

        {/* Boat Photo (Boat 360 Plus) */}
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Camera className="w-5 h-5 text-cyan-500" />
              Boat Photo
              {!isPaid && (
                <Badge className="bg-gradient-to-r from-cyan-500 to-blue-500 text-xs ml-2">
                  <Crown className="w-3 h-3 mr-1" />
                  Plus
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isPaid ? (
              <div className="space-y-4">
                {/* Photo preview */}
                <div className="flex justify-center">
                  {boatPhoto ? (
                    <div className="relative">
                      <img 
                        src={boatPhoto} 
                        alt="Your boat" 
                        className="w-32 h-32 rounded-lg object-cover border-2 border-cyan-200 shadow-md"
                        data-testid="boat-photo-preview"
                      />
                      <button
                        onClick={handleDeletePhoto}
                        disabled={photoLoading}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors"
                        data-testid="delete-photo-btn"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-32 h-32 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400">
                      <Ship className="w-8 h-8 mb-1" />
                      <span className="text-xs">No photo</span>
                    </div>
                  )}
                </div>
                
                {/* Upload button */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoSelect}
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif,.jpg,.jpeg,.png,.webp,.heic,.heif,.gif"
                  className="hidden"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={photoLoading}
                  variant="outline"
                  className="w-full"
                  data-testid="upload-photo-btn"
                >
                  {photoLoading ? (
                    <>Uploading...</>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      {boatPhoto ? 'Change Photo' : 'Upload Photo'}
                    </>
                  )}
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  Shown as your marker on the map • Max 5MB • auto-resized
                </p>

                {/* Who can see the boat photo on the map */}
                {boatPhoto && (
                  <div className="pt-2 border-t border-gray-100" data-testid="photo-visibility-section">
                    <Label className="text-sm font-medium flex items-center gap-1 mb-2">
                      <Eye className="w-4 h-4 text-cyan-500" />
                      Who can see my boat on the map
                    </Label>
                    <RadioGroup value={photoVisibility} onValueChange={handlePhotoVisibilityChange} className="gap-2">
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="crew" id="pv-crew" data-testid="photo-vis-crew" />
                        <Label htmlFor="pv-crew" className="text-sm font-normal cursor-pointer">
                          Crew only <span className="text-gray-400">(just your friends)</span>
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="public" id="pv-public" data-testid="photo-vis-public" />
                        <Label htmlFor="pv-public" className="text-sm font-normal cursor-pointer">
                          Everyone <span className="text-gray-400">(all boaters nearby)</span>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Camera className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  Add a boat photo so friends can identify you on the water
                </p>
                <p className="text-xs text-cyan-600 flex items-center justify-center gap-1">
                  <Crown className="w-3 h-3" />
                  Boat 360 Plus feature
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Boat Draft - For shallow water warnings */}
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Anchor className="w-5 h-5 text-cyan-500" />
              Boat Draft
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Enter your boat's draft (depth below waterline) to receive shallow water warnings during navigation.
              </p>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="50"
                  placeholder="e.g., 3.5"
                  value={boatDraftFt}
                  onChange={(e) => setBoatDraftFt(e.target.value)}
                  className="w-32"
                  data-testid="boat-draft-input"
                />
                <span className="text-gray-600 font-medium">feet</span>
              </div>
              <Button
                onClick={handleSaveDraft}
                disabled={draftSaving}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="save-draft-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {draftSaving ? 'Saving...' : 'Save Draft'}
              </Button>
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                <p className="text-xs text-blue-700">
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  When navigating, you'll be warned if your route crosses water shallower than your draft + 2ft safety margin.
                </p>
              </div>
              {!boatDraftFt && (
                <p className="text-xs text-amber-600">
                  No draft set - shallow water warnings are disabled
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Referral Program */}
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Gift className="w-5 h-5 text-green-500" />
              Refer Friends & Earn $5
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Share your referral code with friends. When they sign up and upgrade to Boat 360 Plus, you earn $5!
              </p>
              
              {user?.referral_code ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-100 rounded-lg p-3 font-mono text-lg text-center font-bold tracking-wider">
                      {user.referral_code}
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        navigator.clipboard.writeText(user.referral_code);
                        toast.success('Code copied!');
                      }}
                      data-testid="copy-referral-btn"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      const text = `Join me on Boat 360! Use my referral code ${user.referral_code} when you sign up. Download at: https://play.google.com/store/apps/details?id=com.boat360.app`;
                      if (navigator.share) {
                        navigator.share({ text });
                      } else {
                        navigator.clipboard.writeText(text);
                        toast.success('Share message copied!');
                      }
                    }}
                    data-testid="share-referral-btn"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share with Friends
                  </Button>
                  
                  {user.referral_count > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                      <p className="text-green-700 font-medium">
                        🎉 {user.referral_count} friend{user.referral_count > 1 ? 's' : ''} signed up!
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">Referral code loading...</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Visibility */}
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Eye className="w-5 h-5 text-cyan-500" />
              Visibility Mode
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={visibilityMode}
              onValueChange={setVisibilityMode}
              className="space-y-3"
            >
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="INVISIBLE" id="invisible" data-testid="visibility-invisible" />
                <div>
                  <Label htmlFor="invisible" className="font-medium">
                    Invisible
                  </Label>
                  <p className="text-sm text-gray-500">No one can see you on the map</p>
                </div>
              </div>
              <Separator />
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="FRIENDS_ONLY" id="friends-only" data-testid="visibility-friends" />
                <div>
                  <Label htmlFor="friends-only" className="font-medium">
                    Friends Only
                  </Label>
                  <p className="text-sm text-gray-500">Only friends can see your location</p>
                </div>
              </div>
              <Separator />
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="PUBLIC_ANON" id="public-anon" data-testid="visibility-public-anon" />
                <div>
                  <Label htmlFor="public-anon" className="font-medium">
                    Public (Anonymous)
                  </Label>
                  <p className="text-sm text-gray-500">
                    Everyone sees you as anonymous dot (no name)
                  </p>
                </div>
              </div>
              <Separator />
              <div className="flex items-start space-x-3">
                <RadioGroupItem
                  value="PUBLIC_PLUS_FRIENDS"
                  id="public-friends"
                  data-testid="visibility-public-friends"
                />
                <div>
                  <Label htmlFor="public-friends" className="font-medium">
                    Public + Friends
                  </Label>
                  <p className="text-sm text-gray-500">
                    Anonymous to public, full details to friends
                  </p>
                </div>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Units */}
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Gauge className="w-5 h-5 text-cyan-500" />
              Speed Units
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RadioGroup value={units} onValueChange={setUnits} className="flex gap-4">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="KNOTS" id="knots" data-testid="units-knots" />
                <Label htmlFor="knots">Knots</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="MPH" id="mph" data-testid="units-mph" />
                <Label htmlFor="mph">MPH</Label>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={isLoading}
          className="w-full bg-cyan-500 hover:bg-cyan-600 mb-4"
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {isLoading ? 'Saving...' : 'Save Changes'}
        </Button>

        {/* Admin Panel Access (for admins only) */}
        {user?.is_admin && (
          <Card className="mb-4 border-purple-200 bg-purple-50">
            <CardContent className="p-4">
              <Button
                onClick={onOpenAdmin}
                variant="outline"
                className="w-full border-purple-400 text-purple-700 hover:bg-purple-100"
                data-testid="admin-panel-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                Admin Panel
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Notification Settings */}
        <div className="mb-4">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Notifications & Tracking
          </h2>
          <NotificationSettings />
        </div>

        {/* Logout */}
        <Card className="border-red-200">
          <CardContent className="p-4">
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full border-red-300 text-red-600 hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log Out
            </Button>
          </CardContent>
        </Card>

        {/* Delete Account */}
        <Card className="mt-4 border-red-300 bg-red-50">
          <CardContent className="p-4">
            <h3 className="font-semibold text-red-800 text-sm mb-2">Delete Account</h3>
            <p className="text-xs text-red-600 mb-3">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            <Button
              onClick={handleDeleteAccount}
              disabled={deleteLoading}
              variant="outline"
              className="w-full border-red-500 text-red-700 hover:bg-red-100"
              data-testid="delete-account-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {deleteLoading ? 'Deleting...' : 'Delete My Account'}
            </Button>
          </CardContent>
        </Card>

        {/* Safety & Legal Disclaimer */}
        <Card className="mt-4 bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800 text-sm mb-1">Important Safety Notice</h3>
                <p className="text-xs text-amber-700 leading-relaxed">
                  Boat 360 is designed as a supplementary tool for communication and awareness only. 
                  Depth data, charts, and location information provided are approximate and <strong>not 
                  intended for navigation purposes</strong>.
                </p>
                <p className="text-xs text-amber-700 leading-relaxed mt-2">
                  Always rely on official nautical charts, proper depth-finding equipment, and 
                  established navigation practices. This app does not replace professional marine 
                  navigation instruments or Coast Guard services.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* App Info */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-cyan-100 rounded-full mb-2">
            <Ship className="w-6 h-6 text-cyan-600" />
          </div>
          <p className="text-sm font-medium text-gray-600">Boat 360</p>
          <p className="text-xs text-gray-400">Version 2.0.0</p>
        </div>
      </ScrollArea>
    </div>
  );
};

export default SettingsScreen;
